import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Dropdown,
  IconButton,
  Icons,
  Tooltip,
} from '@commenda/kakashi';
import { cn } from '@commenda/utils';
import { useEffect, useRef, useState, type ComponentProps } from 'react';
import {
  extractTableDataFromElement,
  tableDataToCSV,
  tableDataToMarkdown,
} from 'streamdown';

type MarkdownTableProps = ComponentProps<'table'> & { node?: unknown };

// Mirrors packages/kakashi Table primitives so chat tables read like the rest of the app.
const TABLE_CLASS = 'min-w-full table-auto divide-y divide-border-primary';

function triggerDownload(content: string, fileName: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}

/** Renders a markdown table as a bg-light card (toolbar + bordered table box inside) with kakashi
 * Table styling. Toolbar: copy (as markdown), export (kakashi Dropdown → CSV / Markdown) and a
 * fullscreen preview, all as kakashi IconButtons — same treatment as the message actions. Replaces
 * streamdown's default table chrome, whose controls render unstyled in this app. */
export function MarkdownTable({
  children,
  node: _node,
  className,
  ...rest
}: MarkdownTableProps) {
  const tableRef = useRef<HTMLTableElement>(null);
  const [copied, setCopied] = useState(false);
  const [isFullscreenOpen, setFullscreenOpen] = useState(false);
  const copyResetRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(copyResetRef.current), []);

  const handleCopy = () => {
    if (!tableRef.current) return;
    const data = extractTableDataFromElement(tableRef.current);
    navigator.clipboard
      .writeText(tableDataToMarkdown(data))
      .then(() => {
        setCopied(true);
        clearTimeout(copyResetRef.current);
        copyResetRef.current = setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => undefined);
  };

  const handleDownload = (format: 'csv' | 'markdown') => {
    if (!tableRef.current) return;
    const data = extractTableDataFromElement(tableRef.current);
    if (format === 'csv')
      triggerDownload(tableDataToCSV(data), 'table.csv', 'text/csv');
    else
      triggerDownload(tableDataToMarkdown(data), 'table.md', 'text/markdown');
  };

  return (
    <>
      <div className="my-lg flex flex-col gap-md rounded-xl border border-border-primary bg-bg-light p-md">
        <div className="flex items-center justify-end gap-xs">
          <Tooltip
            content={copied ? 'Copied' : 'Copy table'}
            side="top"
            asChild
          >
            <IconButton
              size="sm"
              variant="default"
              aria-label="Copy table"
              onClick={handleCopy}
              icon={
                copied ? (
                  <Icons.Check className="size-4" aria-hidden="true" />
                ) : (
                  <Icons.Copy06 className="size-4" aria-hidden="true" />
                )
              }
            />
          </Tooltip>
          <Dropdown
            side="bottom"
            align="end"
            sideOffset={4}
            contentClassName="w-40"
            triggerTooltip="Download table"
            menu={{
              groups: [
                {
                  items: [
                    {
                      label: 'CSV',
                      onClick: () => handleDownload('csv'),
                    },
                    {
                      label: 'Markdown',
                      onClick: () => handleDownload('markdown'),
                    },
                  ],
                },
              ],
            }}
          >
            <IconButton
              size="sm"
              variant="default"
              aria-label="Download table"
              icon={<Icons.Download01 className="size-4" aria-hidden="true" />}
            />
          </Dropdown>
          <Tooltip content="Fullscreen" side="top" asChild>
            <IconButton
              size="sm"
              variant="default"
              aria-label="View table fullscreen"
              onClick={() => setFullscreenOpen(true)}
              icon={<Icons.Maximize01 className="size-4" aria-hidden="true" />}
            />
          </Tooltip>
        </div>
        <div className="overflow-x-auto rounded-xl border border-border-primary bg-bg-primary">
          <table
            ref={tableRef}
            className={cn(TABLE_CLASS, className)}
            {...rest}
          >
            {children}
          </table>
        </div>
      </div>

      <Dialog
        open={isFullscreenOpen}
        onOpenChange={(open) => {
          if (!open) setFullscreenOpen(false);
        }}
      >
        <DialogContent
          className="flex max-h-[90vh] w-[90vw] max-w-6xl flex-col"
          preventCloseOnOutsideClick={false}
        >
          <DialogHeader>
            <DialogTitle>Table</DialogTitle>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-auto p-3xl">
            <table className={TABLE_CLASS}>{children}</table>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
